/**
 * Copy helpers for the install confirmation modal. Pure functions, unit-tested
 * in `__tests__/install-copy.test.ts`.
 *
 * Non-technical users are the audience: item slugs become human names, the
 * headline is composed from the ability's real counts (never hardcoded), and
 * "MCP server" reads as "connection".
 */

/** Kebab words whose human casing isn't Title Case. Small on purpose — the
 *  generic title-case covers everything else. */
const CASED_WORDS: Record<string, string> = {
  id: "ID",
  "3d": "3D",
  youtube: "YouTube",
  tiktok: "TikTok",
  ai: "AI",
  ugc: "UGC",
  seo: "SEO",
  pdf: "PDF",
  url: "URL",
  api: "API",
  mcp: "MCP",
};

/**
 * Human name for a bundled item. "higgsfield-youtube-thumbnail" (with the
 * ability slug "higgsfield") → "YouTube Thumbnail".
 *
 * - Strips `<abilitySlug>-` when the item slug carries it.
 * - Title-cases the kebab remainder, with sensible casing for initialisms.
 * - A name that already reads human (spaces or capitals) returns verbatim,
 *   so real display names are never mangled.
 */
export function prettifyItemName(raw: string, abilitySlug?: string): string {
  if (!raw) return raw;
  // Already human — leave it alone.
  if (/[A-Z\s]/.test(raw)) return raw;
  const prefix = abilitySlug ? `${abilitySlug}-` : "";
  const base =
    prefix && raw.startsWith(prefix) && raw.length > prefix.length
      ? raw.slice(prefix.length)
      : raw;
  return base
    .split(/[-_]+/)
    .filter(Boolean)
    .map(
      (word) =>
        CASED_WORDS[word] ?? word.charAt(0).toUpperCase() + word.slice(1)
    )
    .join(" ");
}

/** "A and B" / "A, B and C" — plain product prose, no oxford comma. */
function joinList(parts: string[]): string {
  if (parts.length <= 1) return parts[0] ?? "";
  return `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
}

const plural = (n: number, word: string): string =>
  `${n} ${word}${n === 1 ? "" : "s"}`;

/**
 * The modal's headline, composed from the real counts:
 * "8 skills and 1 connection for your agent." Hooks ride a trailing clause
 * ("plus N behaviors that run automatically when their triggers fire") since
 * they change agent behaviour rather than adding capability surface.
 */
export function installSummaryLine(
  counts: { skills: number; agents: number; mcps: number },
  behaviorCount: number
): string {
  const parts: string[] = [];
  if (counts.skills > 0) parts.push(plural(counts.skills, "skill"));
  if (counts.agents > 0) parts.push(plural(counts.agents, "agent"));
  if (counts.mcps > 0) parts.push(plural(counts.mcps, "connection"));

  const behaviors =
    behaviorCount > 0
      ? behaviorCount === 1
        ? ", plus 1 behavior that runs automatically when its triggers fire"
        : `, plus ${behaviorCount} behaviors that run automatically when their triggers fire`
      : "";

  if (parts.length === 0)
    return `Adds this ability to your workspace${behaviors}.`;
  return `${joinList(parts)} for your agent${behaviors}.`;
}
